import styled from '@emotion/styled';
import type { NETWORK_MODE } from '@racemap/sdk/schema/trackers';
import { Tag, Typography } from 'antd';
import type { FC } from 'react';
import { IconGlobe, IconMobile, IconSignal } from '../Icon';

const { Text } = Typography;

const networkModeLabels: Record<string, string> = {
  NONE: 'None',
  AUTO: 'Auto',
  GSM_ONLY: 'GSM Only',
  LTE_ONLY: 'LTE Only',
  GSM_FIRST: 'GSM First',
};
const lteModeLabels: Record<string, string> = {
  NONE: 'None',
  CAT_M1_AND_CAT_NB2_M1_FIRST: 'Cat-M1 & Cat-NB2 (Cat-M1 first)',
  CAT_M1_AND_CAT_NB2_NB2_FIRST: 'Cat-M1 & Cat-NB2 (Cat-NB2 first)',
  CAT_M1: 'Cat-M1',
  CAT_NB2: 'Cat-NB2',
  CAT_NB2_FIRST: 'Cat-NB2 First',
  CAT_NB2_ONLY: 'Cat-NB2 Only',
};

const searchOrders: Partial<Record<NETWORK_MODE, Array<string>>> = {
  AUTO__CAT_M1_AND_CAT_NB2_M1_FIRST: ['M1', '2G', 'NB2'],
  AUTO__CAT_M1_AND_CAT_NB2_NB2_FIRST: ['2G', 'NB2', 'M1'],
  AUTO__CAT_M1: ['M1', '2G'],
  AUTO__CAT_NB2: ['2G', 'NB2'],
  AUTO__CAT_NB2_FIRST: ['NB2', '2G', 'M1'],
  AUTO__CAT_NB2_ONLY: ['NB2', '2G'],

  GSM_ONLY: ['2G'],

  LTE_ONLY__CAT_M1_AND_CAT_NB2_M1_FIRST: ['M1', 'NB2'],
  LTE_ONLY__CAT_M1_AND_CAT_NB2_NB2_FIRST: ['NB2', 'M1'],
  LTE_ONLY__CAT_M1: ['M1'],
  LTE_ONLY__CAT_NB2: ['NB2'],

  GSM_FIRST__CAT_M1_AND_CAT_NB2_M1_FIRST: ['2G', 'M1', 'NB2'],
  GSM_FIRST__CAT_M1_AND_CAT_NB2_NB2_FIRST: ['2G', 'NB2', 'M1'],
  GSM_FIRST__CAT_M1: ['2G', 'M1'],
  GSM_FIRST__CAT_NB2: ['2G', 'NB2'],
};

const NetworkTypeConfig: Record<string, { color: string; icon: JSX.Element }> = {
  '2G': { color: 'orange', icon: <IconSignal /> },
  'M1': { color: 'blue', icon: <IconMobile /> },
  'NB2': { color: 'green', icon: <IconGlobe /> },
};

interface Props {
  networkMode: NETWORK_MODE;
}

export const NetworkAndLteMode: FC<Props> = ({ networkMode }) => {
  const searchOrder = searchOrders[networkMode] ?? [];
  const [mode, lteMode] = networkMode.split('__');
  const networkModeLabel = networkModeLabels[mode] ?? 'N/A';
  const lteModeLabel = lteModeLabels[lteMode] ?? 'N/A';
  return (
    <Container>
      <Section>
        <Label>Mode:</Label>
        <div>{networkModeLabel}</div>
      </Section>
      <Section>
        <Label>LTE:</Label>
        <div>{lteModeLabel}</div>
      </Section>
      <Section>
        <Label>Search Order:</Label>
        {searchOrder.length > 0 ? (
          <>
            {searchOrder.map((networkType) => {
              const { color, icon } = NetworkTypeConfig[networkType];
              return (
                <NetworkType key={networkType}>
                  <div key={networkType}>{`${searchOrder.indexOf(networkType) + 1} :`}</div>
                  <Tag key={networkType} color={color} icon={icon}>
                    <NetworkTypeLabel>{networkType}</NetworkTypeLabel>
                  </Tag>
                </NetworkType>
              );
            })}
          </>
        ) : (
          <div>N/A</div>
        )}
      </Section>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
`;

const NetworkType = styled.div`
  display: flex;
  flex-direction: row;
  padding: 5px;
  gap: 5px;
`;

const NetworkTypeLabel = styled.div`
  display: inline-flex;
  align-items: center;
  padding-left: 4px;
`;

const Label = styled(Text)`
  font-weight: bold;
`;
